/**
 * Auth Hook
 *
 * Manages authentication state using localStorage.
 */

import { useState, useEffect, useCallback, createContext, useContext } from "react";
import type { ReactNode } from "react";

// ============================================================================
// Types
// ============================================================================

interface AuthState {
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
}

interface AuthContextValue extends AuthState {
    login: (token: string) => void;
    logout: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const AUTH_STORAGE_KEY = "cloudledger_auth_token";

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const [state, setState] = useState<AuthState>({
        token: null,
        isAuthenticated: false,
        isLoading: true,
    });

    // Load token from localStorage on mount
    useEffect(() => {
        const stored = localStorage.getItem(AUTH_STORAGE_KEY);
        setState({
            token: stored,
            isAuthenticated: !!stored,
            isLoading: false,
        });
    }, []);

    const login = useCallback((token: string) => {
        localStorage.setItem(AUTH_STORAGE_KEY, token);
        setState({
            token,
            isAuthenticated: true,
            isLoading: false,
        });
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        setState({
            token: null,
            isAuthenticated: false,
            isLoading: false,
        });
    }, []);

    return (
        <AuthContext.Provider value={{ ...state, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access auth state and methods
 *
 * @example
 * ```tsx
 * const { token, isAuthenticated, login, logout } = useAuth();
 * ```
 */
export const useAuth = (): AuthContextValue => {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error("useAuth must be used within AuthProvider");
    }

    return context;
};
